import React, { useCallback, useMemo, useState } from 'react';

import Textarea from 'components/Textarea/Textarea';

import { SchemaType } from '../../../integration-test-modal.types';
import { schemaUtils } from '../../../integration-test-modal.utils';
import { IJSONFieldProps } from '../schema-form-field.types';

import { jsonFieldUtils } from './json-field.utils';

export const JSONField: React.FC<IJSONFieldProps> = ({
  name,
  path,
  property,
  value,
  onChange,
  required
}) => {
  const fieldLabel = useMemo(
    () => schemaUtils.formatFieldLabel(name, property.title),
    [property.title, name]
  );

  const isArray = property.type === SchemaType.ARRAY;

  const [localValue, setLocalValue] = useState<string>('');
  const [isEditing, setIsEditing] = useState(false);

  const normalizedValue = useMemo(() => {
    const defaultValue = jsonFieldUtils.getDefaultValue(isArray);
    if (isArray) {
      return jsonFieldUtils.getNormalizedArrayValue(value, defaultValue);
    }
    return jsonFieldUtils.getNormalizedObjectValue(value, defaultValue);
  }, [value, isArray]);

  const displayValue = useMemo(() => {
    if (isEditing) {
      return localValue;
    }
    return jsonFieldUtils.getStringifiedValue(normalizedValue);
  }, [normalizedValue, isEditing, localValue]);

  const handleFocus = useCallback(() => {
    setIsEditing(true);
    setLocalValue(JSON.stringify(normalizedValue, null, 2));
  }, [normalizedValue]);

  const handleBlur = useCallback(() => {
    setIsEditing(false);
    const parsedValue = jsonFieldUtils.parseJSONValue(localValue, normalizedValue);
    onChange(path, parsedValue);
  }, [localValue, path, onChange, normalizedValue]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setLocalValue(e.target.value);
    },
    []
  );

  const descriptionText = useMemo(() => {
    const helperText = jsonFieldUtils.getHelperText(isArray);
    if (property.description) {
      return `${property.description} - ${helperText}`;
    }
    return helperText;
  }, [property.description, isArray]);

  return (
    <Textarea
      label={fieldLabel}
      isRequired={required}
      name={descriptionText}
      value={displayValue}
      onChange={handleInputChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      placeholder={jsonFieldUtils.getPlaceholderText(isArray)}
      readOnly={property.readOnly}
      disabled={property.readOnly}
      required={required}
    />
  );
};
